import { useMemo } from "react";
import { formatTimeAgo } from "../utils/date";
import { WorkspaceType } from "../components/chat-companion/types";
import type { WorkspaceSidebarWorkspace } from "../components/chat-companion/types";

export const HOME_ID = "home";
export const GENERAL_WORKSPACE_ID = "general_chats";
export const TODO_WORKSPACE_ID = "todo";
export const LEARNING_ACTIVITIES_WORKSPACE_ID = "learning_activities";
export const SETTINGS_WORKSPACE_ID = "settings";

type CompanionWorkspaces = {
  generalChats: Array<{ last_modified?: string | null } | null>;
  generalChatsCount?: number | null;
  courseWorkspaces: Array<{
    id: string;
    name: string;
    totalCount?: number | null;
    chats?: Array<{ last_modified?: string | null } | null> | null;
  }>;
};

export const homeWorkspace: WorkspaceSidebarWorkspace = {
  id: HOME_ID,
  title: "Home",
  type: "home" as WorkspaceType,
};

const TODO_WORKSPACES: WorkspaceSidebarWorkspace[] = [
  {
    id: TODO_WORKSPACE_ID,
    title: "To-Dos",
    type: "todo" as WorkspaceType,
  },
];

const LEARNING_ACTIVITIES_WORKSPACES: WorkspaceSidebarWorkspace[] = [
  {
    id: LEARNING_ACTIVITIES_WORKSPACE_ID,
    title: "Learning Activities",
    type: "learning_activities" as WorkspaceType,
  },
];

const SETTINGS_WORKSPACES: WorkspaceSidebarWorkspace[] = [
  {
    id: SETTINGS_WORKSPACE_ID,
    title: "Settings",
    type: "settings" as WorkspaceType,
  },
];

const getLastActivityLabel = (
  chats: Array<{ last_modified?: string | null } | null> | null | undefined,
  allowUndefined?: boolean
) => {
  const lastModified = chats?.[0]?.last_modified;
  if (!lastModified) {
    return allowUndefined ? undefined : "";
  }
  return formatTimeAgo(lastModified);
};

export function useWorkspaceLists({
  generalChats,
  generalChatsCount,
  courseWorkspaces,
  activeWorkspaceId,
}: {
  generalChats: CompanionWorkspaces["generalChats"];
  generalChatsCount?: CompanionWorkspaces["generalChatsCount"];
  courseWorkspaces: CompanionWorkspaces["courseWorkspaces"];
  activeWorkspaceId: string | null;
}) {
  const generalWorkspaces = useMemo(() => {
    return [
      {
        id: GENERAL_WORKSPACE_ID,
        title: "General Chat",
        type: "general" as WorkspaceType,
        threadCount: generalChatsCount ?? generalChats.length,
        lastActivityLabel: getLastActivityLabel(generalChats, true),
        isActive: activeWorkspaceId === GENERAL_WORKSPACE_ID,
      },
    ];
  }, [generalChats, generalChatsCount, activeWorkspaceId]);

  const courseWorkspaceItems = useMemo(() => {
    if (courseWorkspaces.length === 0) return [];

    return courseWorkspaces.map((workspace) => ({
      id: workspace.id,
      title: workspace.name,
      type: "course" as WorkspaceType,
      threadCount: workspace.totalCount ?? workspace.chats?.length ?? 0,
      lastActivityLabel: getLastActivityLabel(workspace.chats),
      isActive: activeWorkspaceId === workspace.id,
    }));
  }, [courseWorkspaces, activeWorkspaceId]);

  return {
    generalWorkspaces,
    courseWorkspaceItems,
    todoWorkspaces: TODO_WORKSPACES,
    learningActivitiesWorkspaces: LEARNING_ACTIVITIES_WORKSPACES,
    settingsWorkspaces: SETTINGS_WORKSPACES,
  };
}
